/**
 * TemplateLibrary Store
 *
 * Manages UI state for the template library, including which version (draft vs production)
 * the user is viewing for each template.
 */

import { type SetStoreState, createStore, createStoreInstance } from '@/stores/createStore';

export interface TemplateLibraryState {
  /** Per-template toggle: true = viewing draft, false/absent = viewing production. */
  draftToggles: Record<string, boolean>;
}

const initialState: TemplateLibraryState = {
  draftToggles: {},
};

const templateLibraryActionsFactory = (set: SetStoreState<TemplateLibraryState>) => ({
  /** Mark a template as viewing draft or production in the library. */
  setDraftToggle: (templateId: string, isViewingDraft: boolean) =>
    set((draft: TemplateLibraryState) => {
      draft.draftToggles[templateId] = isViewingDraft;
    }),

  /** Clear the draft toggle for a template (e.g., after lock+publish removes the draft). */
  clearDraftToggle: (templateId: string) =>
    set((draft: TemplateLibraryState) => {
      delete draft.draftToggles[templateId];
    }),

  reset: () => set(() => initialState),
});

const [TemplateLibraryStoreProvider, useStore, useSelector] = createStore(
  'TemplateLibrary',
  initialState,
  templateLibraryActionsFactory,
);

export { TemplateLibraryStoreProvider };

/** Per-template draft/production toggle state. */
export const useDraftToggles = () => useSelector((state) => state.draftToggles);

/** Actions for managing draft toggle state. */
export const useTemplateLibraryActions = () => useStore().actions;

/** Create an isolated store instance for tests that need to assert exact store state. */
export const createTestTemplateLibraryStore = () => createStoreInstance(initialState, templateLibraryActionsFactory);
